// Client for the oc_analyst ADK agent on Cloud Run. Speaks the standard ADK
// REST API: one session per browser chat, /run_sse for streamed replies.
// Requests carry the Auth0 id_token, which the agent service verifies.

const APP_NAME = "oc_analyst";

const baseUrl = (): string => {
  const url = process.env.REACT_APP_OC_ANALYST_URL;
  if (!url) {
    throw new Error("REACT_APP_OC_ANALYST_URL is not configured");
  }
  return url.replace(/\/+$/, "");
};

export type AdkPart = {
  text?: string;
  functionCall?: { name?: string };
  functionResponse?: { name?: string };
};

export type AdkEvent = {
  author?: string;
  partial?: boolean;
  content?: { role?: string; parts?: AdkPart[] };
  errorMessage?: string;
};

export const newSessionId = (): string =>
  `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const createChatSession = async (
  token: string,
  userId: string
): Promise<string> => {
  const sessionId = newSessionId();
  const response = await fetch(
    `${baseUrl()}/apps/${APP_NAME}/users/${encodeURIComponent(
      userId
    )}/sessions/${sessionId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    }
  );
  if (!response.ok) {
    throw new Error(`Could not create chat session (HTTP ${response.status})`);
  }
  return sessionId;
};

export async function* streamAgentReply(
  token: string,
  userId: string,
  sessionId: string,
  text: string
): AsyncGenerator<AdkEvent> {
  const response = await fetch(`${baseUrl()}/run_sse`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      app_name: APP_NAME,
      user_id: userId,
      session_id: sessionId,
      new_message: { role: "user", parts: [{ text }] },
      streaming: true,
    }),
  });
  if (!response.ok || !response.body) {
    throw new Error(`Agent request failed (HTTP ${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by a blank line; payload lines start "data:".
    let separator = buffer.indexOf("\n\n");
    while (separator !== -1) {
      const rawEvent = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const data = rawEvent
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .join("");
      if (data) {
        try {
          yield JSON.parse(data) as AdkEvent;
        } catch {
          // ignore malformed chunks
        }
      }
      separator = buffer.indexOf("\n\n");
    }
  }
}
